import {
  type TboCityListItem,
  type TboCountryListItem,
  type TboHotelListItem,
  type TboStateListItem,
} from '@utazzo/shared';
import { UzButton, UzInput, UzSelect, UzTabs, UzTooltip } from '@utazzo/ui';
import { Check, RefreshCw, Star } from 'lucide-react';
import React, { useMemo, useState } from 'react';

import { TboHotelSyncPanel } from '../components/TboHotelSyncPanel';
import {
  useSyncTbo,
  useTboCitiesList,
  useTboCountriesList,
  useTboHotelsList,
  useTboHotelStatus,
  useTboStatesList,
  useTboStatus,
} from '../use-tbo';

import { DataGrid, type DataGridColumn } from '@/components/data-grid';
import { PageShell } from '@/components/page-shell';
import { useCurrentUser } from '@/features/auth/use-auth';
import { useDataGridState } from '@/hooks/use-data-grid-state';

/// 2026-07-06 ADR-0059. New `hotels` tab surfaces the `tbo_hotels`
/// mirror (populated by search-lazy backfill + this tab's manual
/// per-city sync button). Sits alongside the existing geography
/// tabs so ops has one screen for all TBO reference data.
type Tab = 'countries' | 'states' | 'cities' | 'hotels';
const MIN_SEARCH_LENGTH = 3;

export const TboGeographyPage = (): React.JSX.Element => {
  const me = useCurrentUser();
  const canSync = me?.role === 'ADMIN';

  const [tab, setTab] = useState<Tab>('countries');
  const gridState = useDataGridState({
    defaultPageSize: 20,
    defaultSortBy: 'name',
    defaultSortDir: 'asc',
  });
  const { state } = gridState;
  const sync = useSyncTbo();
  const statusQuery = useTboStatus();

  // Country/city geography is a once-per-DB sync - disable the button after the
  // first successful sync (hotel sync, which IS recurring, lands later).
  const alreadySynced = (statusQuery.data?.countries ?? 0) > 0;
  const lastSyncedAt = statusQuery.data?.lastSyncedAt
    ? new Date(statusQuery.data.lastSyncedAt).toLocaleString()
    : null;

  const base = useMemo(() => {
    const term = state.q.trim();
    return {
      page: state.page,
      pageSize: state.pageSize,
      sortBy: state.sortBy ?? 'name',
      sortDir: state.sortDir,
      ...(term.length >= MIN_SEARCH_LENGTH ? { search: term } : {}),
    };
  }, [state]);

  const countriesQuery = useTboCountriesList(base, tab === 'countries');
  const statesQuery = useTboStatesList(base, tab === 'states');
  const citiesQuery = useTboCitiesList(base, tab === 'cities');

  // 2026-07-06 ADR-0059. Hotels tab state. Filters are simple + all
  // server-side (see TboHotelListQuerySchema in @utazzo/shared):
  //   - countryCode: 2-letter ISO (client-side text input; TBO uses
  //     upper-case codes, so we upper-case before sending)
  //   - minStarRating: 1..5 (dropdown)
  //   - search (via the shared DataGrid state): name or hotelCode
  // The sync form is a small two-input row above the grid: country
  // code (required) + optional TBO city id. Fires the same
  // POST /tbo-hotel/sync as the runbook, but through the UI.
  const [hotelCountryFilter, setHotelCountryFilter] = useState('');
  const [hotelStarFilter, setHotelStarFilter] = useState<string>('');
  const hotelBase = useMemo(() => {
    const term = state.q.trim();
    return {
      page: state.page,
      pageSize: state.pageSize,
      sortBy: state.sortBy ?? 'name',
      sortDir: state.sortDir,
      ...(term.length >= MIN_SEARCH_LENGTH ? { search: term } : {}),
      ...(hotelCountryFilter.trim().length >= 2
        ? { countryCode: hotelCountryFilter.trim().toUpperCase() }
        : {}),
      ...(hotelStarFilter && hotelStarFilter !== 'any'
        ? { minStarRating: Number(hotelStarFilter) }
        : {}),
    };
  }, [state, hotelCountryFilter, hotelStarFilter]);
  const hotelsQuery = useTboHotelsList(hotelBase, tab === 'hotels');
  const hotelStatusQuery = useTboHotelStatus();

  const onStateChange = {
    setSearch: gridState.setSearch,
    setFilter: gridState.setFilter,
    setSort: gridState.setSort,
    setPage: gridState.setPage,
    setPageSize: gridState.setPageSize,
    setDensity: gridState.setDensity,
    setHidden: gridState.setHidden,
  };

  const pageMeta = (q: {
    data?: { pagination: { page: number; pageSize: number; totalItems: number } };
  }) =>
    q.data
      ? {
          page: q.data.pagination.page,
          pageSize: q.data.pagination.pageSize,
          totalItems: q.data.pagination.totalItems,
        }
      : null;

  const countryCols: ReadonlyArray<DataGridColumn<TboCountryListItem>> = useMemo(
    () => [
      {
        id: 'code',
        header: 'Code',
        sortable: true,
        width: '120px',
        cell: (r) => <span className="font-mono">{r.code}</span>,
      },
      {
        id: 'name',
        header: 'Name',
        sortable: true,
        cell: (r) => <span className="font-medium text-zinc-900">{r.name}</span>,
      },
      {
        id: 'syncedAt',
        header: 'Synced',
        cell: (r) => (
          <span className="text-zinc-500">{new Date(r.syncedAt).toLocaleDateString()}</span>
        ),
      },
    ],
    [],
  );
  const stateCols: ReadonlyArray<DataGridColumn<TboStateListItem>> = useMemo(
    () => [
      {
        id: 'name',
        header: 'Name',
        sortable: true,
        cell: (r) => <span className="font-medium text-zinc-900">{r.name}</span>,
      },
      {
        id: 'countryCode',
        header: 'Country',
        width: '120px',
        cell: (r) => <span className="font-mono">{r.countryCode}</span>,
      },
      {
        id: 'id',
        header: 'TBO State Id',
        cell: (r) => <span className="font-mono text-xs text-zinc-500">{r.id}</span>,
      },
    ],
    [],
  );
  // 2026-07-06 ADR-0059. Hotel columns. Kept slim: code, name +
  // stars, city id, country, last synced. Amenities + photos are
  // shown in the itinerary picker, not here (this is a reference-
  // data browser, not a booking surface).
  const hotelCols: ReadonlyArray<DataGridColumn<TboHotelListItem>> = useMemo(
    () => [
      {
        id: 'hotelCode',
        header: 'Code',
        sortable: true,
        width: '110px',
        cell: (r) => <span className="font-mono text-xs">{r.hotelCode}</span>,
      },
      {
        id: 'name',
        header: 'Name',
        sortable: true,
        cell: (r) => (
          <div className="flex items-center gap-2">
            <span className="font-medium text-zinc-900">{r.name}</span>
            {r.starRating !== null && r.starRating > 0 && (
              <span
                className="inline-flex items-center gap-0.5 text-amber-500"
                aria-label={`${r.starRating} star`}
              >
                {Array.from({ length: Math.min(5, r.starRating) }).map((_, i) => (
                  <Star key={i} className="h-3 w-3 fill-current" aria-hidden />
                ))}
              </span>
            )}
          </div>
        ),
      },
      {
        id: 'address',
        header: 'Address',
        cell: (r) =>
          r.address ? (
            <span className="line-clamp-1 text-zinc-500">{r.address}</span>
          ) : (
            <span className="text-zinc-400">-</span>
          ),
      },
      {
        id: 'tboCityId',
        header: 'City Id',
        sortable: true,
        width: '100px',
        cell: (r) => <span className="font-mono text-xs">{r.tboCityId}</span>,
      },
      {
        id: 'countryCode',
        header: 'Country',
        width: '100px',
        cell: (r) => <span className="font-mono">{r.countryCode ?? '-'}</span>,
      },
      {
        id: 'syncedAt',
        header: 'Synced',
        cell: (r) => (
          <span className="text-zinc-500">{new Date(r.syncedAt).toLocaleDateString()}</span>
        ),
      },
    ],
    [],
  );

  const cityCols: ReadonlyArray<DataGridColumn<TboCityListItem>> = useMemo(
    () => [
      {
        id: 'cityId',
        header: 'City Id',
        sortable: true,
        width: '120px',
        cell: (r) => <span className="font-mono">{r.cityId}</span>,
      },
      {
        id: 'name',
        header: 'Name',
        sortable: true,
        cell: (r) => <span className="font-medium text-zinc-900">{r.name}</span>,
      },
      {
        id: 'countryCode',
        header: 'Country',
        width: '110px',
        cell: (r) => <span className="font-mono">{r.countryCode}</span>,
      },
      {
        id: 'stateId',
        header: 'State',
        cell: (r) => <span className="text-zinc-500">{r.stateId ?? '-'}</span>,
      },
    ],
    [],
  );

  const countriesGrid = (
    <DataGrid<TboCountryListItem>
      columns={countryCols}
      data={countriesQuery.data?.data ?? []}
      pagination={pageMeta(countriesQuery)}
      isLoading={countriesQuery.isLoading}
      isError={countriesQuery.isError}
      error={{
        title: 'Failed to load TBO countries',
        description: 'Try syncing or check your network.',
      }}
      onRetry={() => countriesQuery.refetch()}
      state={state}
      onStateChange={onStateChange}
      getRowId={(r) => r.code}
      searchPlaceholder="Search countries (min 3 characters)"
      emptyTitle="No TBO countries"
      emptyDescription="Run Sync to pull the latest TBO geography."
    />
  );
  const statesGrid = (
    <DataGrid<TboStateListItem>
      columns={stateCols}
      data={statesQuery.data?.data ?? []}
      pagination={pageMeta(statesQuery)}
      isLoading={statesQuery.isLoading}
      isError={statesQuery.isError}
      error={{
        title: 'Failed to load TBO states',
        description: 'Try syncing or check your network.',
      }}
      onRetry={() => statesQuery.refetch()}
      state={state}
      onStateChange={onStateChange}
      getRowId={(r) => r.id}
      searchPlaceholder="Search states (min 3 characters)"
      emptyTitle="No TBO states"
      emptyDescription="TBO exposes states only where the city payload carries one."
    />
  );
  // 2026-07-06 ADR-0059. Hotel tab renders:
  //   1. A status strip: mirror row count + last-synced timestamp.
  //   2. A per-city sync form (country + optional city id -> POST
  //      /tbo-hotel/sync). Country-only walks every tbo_cities row
  //      under that country (slow); city id makes it targeted.
  //   3. Client-side filters for country + star (on top of the
  //      grid's own text search on name).
  //   4. The mirror grid.
  const hotelsGrid = (
    <div className="flex flex-col gap-3">
      <div className="rounded-md border border-zinc-200 bg-zinc-50/50 p-3">
        <div className="mb-2 flex items-center justify-between gap-2 text-xs text-zinc-600">
          <span>
            Mirror rows: <strong>{hotelStatusQuery.data?.hotels ?? '-'}</strong>
            {hotelStatusQuery.data?.lastSyncedAt && (
              <>
                {' '}
                &middot; last synced{' '}
                <strong>{new Date(hotelStatusQuery.data.lastSyncedAt).toLocaleString()}</strong>
              </>
            )}
          </span>
          <span className="text-[11px] text-zinc-400">
            Search also lazily populates the mirror on the itinerary picker.
          </span>
        </div>
        <TboHotelSyncPanel canSync={canSync} />
      </div>
      <div className="flex flex-wrap items-end gap-2 rounded-md border border-zinc-200 bg-white p-3">
        <div className="w-28">
          <UzInput
            name="tbo-hotel-filter-country"
            label="Country"
            placeholder="AE"
            value={hotelCountryFilter}
            onChange={(e) => {
              setHotelCountryFilter(e.target.value);
              gridState.setPage(1);
            }}
          />
        </div>
        <div className="w-32">
          <UzSelect<string>
            name="tbo-hotel-filter-star"
            label="Min star"
            options={[
              { value: 'any', label: 'Any' },
              { value: '5', label: '5+' },
              { value: '4', label: '4+' },
              { value: '3', label: '3+' },
              { value: '2', label: '2+' },
              { value: '1', label: '1+' },
            ]}
            value={hotelStarFilter || 'any'}
            onValueChange={(v) => {
              setHotelStarFilter(v ?? '');
              gridState.setPage(1);
            }}
          />
        </div>
      </div>
      <DataGrid<TboHotelListItem>
        columns={hotelCols}
        data={hotelsQuery.data?.data ?? []}
        pagination={pageMeta(hotelsQuery)}
        isLoading={hotelsQuery.isLoading}
        isError={hotelsQuery.isError}
        error={{
          title: 'Failed to load TBO hotels',
          description: 'Try syncing or check your network.',
        }}
        onRetry={() => hotelsQuery.refetch()}
        state={state}
        onStateChange={onStateChange}
        getRowId={(r) => r.hotelCode}
        searchPlaceholder="Search hotels by name (min 3 characters)"
        emptyTitle="No TBO hotels"
        emptyDescription="Sync a city / country to populate the mirror, or use the itinerary picker to lazily backfill it."
      />
    </div>
  );

  const citiesGrid = (
    <DataGrid<TboCityListItem>
      columns={cityCols}
      data={citiesQuery.data?.data ?? []}
      pagination={pageMeta(citiesQuery)}
      isLoading={citiesQuery.isLoading}
      isError={citiesQuery.isError}
      error={{
        title: 'Failed to load TBO cities',
        description: 'Try syncing or check your network.',
      }}
      onRetry={() => citiesQuery.refetch()}
      state={state}
      onStateChange={onStateChange}
      getRowId={(r) => r.cityId}
      searchPlaceholder="Search cities (min 3 characters)"
      emptyTitle="No TBO cities"
      emptyDescription="Run Sync to pull the latest TBO geography."
    />
  );

  return (
    <PageShell
      title="TBO Geography"
      description="Countries, states, and cities from the TBO inventory provider. Used to map City Master rows to a TBO city."
      actions={
        <div className="flex items-center gap-3">
          {lastSyncedAt && (
            <span className="hidden text-xs text-zinc-500 sm:inline">
              Last synced: {lastSyncedAt}
            </span>
          )}
          <UzTooltip
            label={
              alreadySynced
                ? 'Countries & cities are already synced (one-time per database). Hotel sync comes later.'
                : 'Pull TBO countries & cities into the local reference tables.'
            }
          >
            <span>
              <UzButton
                onClick={() => sync.mutate()}
                loading={sync.isPending}
                disabled={!canSync || alreadySynced}
                aria-label="Sync TBO geography"
              >
                {alreadySynced ? (
                  <Check className="mr-2 h-4 w-4" />
                ) : (
                  <RefreshCw className="mr-2 h-4 w-4" />
                )}
                {alreadySynced ? 'Synced' : 'Sync from TBO'}
              </UzButton>
            </span>
          </UzTooltip>
        </div>
      }
    >
      <UzTabs<Tab>
        value={tab}
        onValueChange={(v) => {
          setTab(v);
          gridState.setPage(1);
        }}
        tabs={[
          { value: 'countries', label: 'Countries', content: countriesGrid },
          { value: 'states', label: 'States', content: statesGrid },
          { value: 'cities', label: 'Cities', content: citiesGrid },
          { value: 'hotels', label: 'Hotels', content: hotelsGrid },
        ]}
      />
    </PageShell>
  );
};
